import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { workApiFetch } from "../clients/workClient";
import { useWorkScope } from "../components/WorkScopeProvider";

const sourceSchema = z.object({
  documentType: z.string(),
  sheet: z.string().optional(),
  cells: z.array(z.string()).optional(),
  range: z.string().optional(),
  page: z.number().optional(),
  paragraph: z.number().optional(),
});
const draftItemSchema = z.object({
  text: z.string(),
  source: sourceSchema,
  review: z.enum(["ok", "warning", "needs_review"]),
});
export const procedureDraftSchema = z.object({
  clientDraftId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  estimatedDurationMinutes: z.number().nullable(),
  instructions: z.array(draftItemSchema),
  tools: z.array(draftItemSchema),
  cautions: z.array(draftItemSchema),
  warnings: z.array(z.string()),
});
const candidateSchema = z.object({
  id: z.string().uuid(),
  sourceName: z.string(),
  displayName: z.string(),
  selected: z.boolean(),
  status: z.enum([
    "discovered",
    "building",
    "review",
    "failed",
    "skipped",
    "published",
  ]),
  warnings: z.array(z.string()),
  errorMessage: z.string().nullable(),
  draft: procedureDraftSchema.nullable(),
});
const builderSchema = z.object({
  id: z.string().uuid(),
  filename: z.string(),
  status: z.enum(["review", "published", "failed", "deleted"]),
  documentWarnings: z.array(z.string()),
  candidates: z.array(candidateSchema),
});
const listSchema = z.object({
  defaultProcedureId: z.string().uuid().nullable(),
  defaultProcedure: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
    })
    .nullable(),
  items: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      description: z.string().nullable(),
      status: z.string(),
      currentVersion: z.number().nullable(),
      updatedAt: z.string(),
      category: z.string().nullable(),
      owner: z.string().nullable(),
      team: z.string().nullable(),
    }),
  ),
  totalCount: z.number(),
  facets: z.object({
    categories: z.discriminatedUnion("status", [
      z.object({ status: z.literal("available"), values: z.array(z.string()) }),
      z.object({
        status: z.literal("unavailable"),
        reason: z.literal("not-persisted"),
      }),
    ]),
    owners: z.discriminatedUnion("status", [
      z.object({ status: z.literal("available"), values: z.array(z.string()) }),
      z.object({
        status: z.literal("unavailable"),
        reason: z.literal("not-persisted"),
      }),
    ]),
  }),
});
const detailItemSchema = z.object({
  text: z.string(),
  source: sourceSchema,
});
const detailSchema = z.object({
  canEdit: z.boolean(),
  canDelete: z.boolean(),
  canRestore: z.boolean(),
  canSetDefault: z.boolean(),
  deleted: z.boolean(),
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  status: z.string(),
  currentVersion: z.number(),
  estimatedDurationMinutes: z.number().nullable(),
  publishedAt: z.string(),
  updatedAt: z.string(),
  instructions: z.array(detailItemSchema),
  tools: z.array(detailItemSchema),
  cautions: z.array(detailItemSchema),
  category: z.string().nullable(),
  owner: z.string().nullable(),
  team: z.string().nullable(),
  canonicalPreview: z.discriminatedUnion("status", [
    z.object({
      status: z.literal("unavailable"),
      reason: z.literal("hierarchy-not-persisted"),
    }),
    z.object({
      status: z.literal("available"),
      groups: z.array(
        z.object({
          id: z.string(),
          title: z.string(),
          sections: z.array(
            z.object({
              id: z.string(),
              title: z.string(),
              parentSectionId: z.string().nullable(),
              items: z.array(
                z.object({
                  id: z.string(),
                  label: z.string(),
                  description: z.string().nullable(),
                  type: z.enum([
                    "text",
                    "checkbox",
                    "number",
                    "choice",
                    "photo",
                  ]),
                  required: z.boolean(),
                  parentItemId: z.string().nullable(),
                  unit: z.string().nullable(),
                  options: z.array(z.string()),
                }),
              ),
            }),
          ),
        }),
      ),
    }),
  ]),
});

export type ProcedureDraft = z.infer<typeof procedureDraftSchema>;
export type ProcedureBuilder = z.infer<typeof builderSchema>;
export type ProcedureCandidate = ProcedureBuilder["candidates"][number];
export type ProcedureSummary = z.infer<typeof listSchema>["items"][number];
export type ProcedureDetail = z.infer<typeof detailSchema>;
export type ProcedureView = "active" | "deleted";

async function jsonResponse<T>(
  response: Response,
  parse: (value: unknown) => T,
) {
  const payload: unknown = await response.json();
  if (!response.ok) {
    const message =
      typeof payload === "object" &&
      payload !== null &&
      "error" in payload &&
      typeof payload.error === "string"
        ? payload.error
        : `Request failed (${response.status})`;
    throw new Error(message);
  }
  return parse(payload);
}

function basePath(customerId: string, facilityId: string) {
  return `/v1/customers/${encodeURIComponent(customerId)}/facilities/${encodeURIComponent(facilityId)}`;
}

export async function fetchProcedures(
  customerId: string,
  facilityId: string,
  search: string,
  view: ProcedureView = "active",
) {
  const params = new URLSearchParams();
  if (search.trim()) params.set("search", search.trim());
  if (view !== "active") params.set("view", view);
  const suffix = params.size > 0 ? `?${params.toString()}` : "";
  return jsonResponse(
    await workApiFetch(
      `${basePath(customerId, facilityId)}/procedures${suffix}`,
    ),
    (value) => listSchema.parse(value),
  );
}

export async function fetchProcedureBuilder(
  customerId: string,
  facilityId: string,
  builderId: string,
) {
  return jsonResponse(
    await workApiFetch(
      `${basePath(customerId, facilityId)}/procedure-builders/${builderId}`,
    ),
    (value) => builderSchema.parse(value),
  );
}

export async function fetchProcedureDetail(
  customerId: string,
  facilityId: string,
  procedureId: string,
  view: ProcedureView = "active",
) {
  const suffix = view === "active" ? "" : `?view=${view}`;
  return jsonResponse(
    await workApiFetch(
      `${basePath(customerId, facilityId)}/procedures/${encodeURIComponent(procedureId)}${suffix}`,
    ),
    (value) => detailSchema.parse(value),
  );
}

export async function previewProcedureBuilder(
  customerId: string,
  facilityId: string,
  file: File,
) {
  const form = new FormData();
  form.append("file", file);
  return jsonResponse(
    await workApiFetch(
      `${basePath(customerId, facilityId)}/procedure-builders/preview`,
      { method: "POST", body: form },
    ),
    (value) => builderSchema.parse(value),
  );
}

export async function selectProcedureCandidates(
  customerId: string,
  facilityId: string,
  builderId: string,
  candidateIds: string[],
) {
  return jsonResponse(
    await workApiFetch(
      `${basePath(customerId, facilityId)}/procedure-builders/${builderId}/candidates`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ candidateIds }),
      },
    ),
    (value) => builderSchema.parse(value),
  );
}

export async function buildProcedureCandidate(
  customerId: string,
  facilityId: string,
  builderId: string,
  candidateId: string,
) {
  return jsonResponse(
    await workApiFetch(
      `${basePath(customerId, facilityId)}/procedure-builders/${builderId}/candidates/${candidateId}/build`,
      { method: "POST" },
    ),
    (value) => builderSchema.parse(value),
  );
}

export async function saveProcedureDraft(
  customerId: string,
  facilityId: string,
  builderId: string,
  candidateId: string,
  draft: ProcedureDraft,
) {
  return jsonResponse(
    await workApiFetch(
      `${basePath(customerId, facilityId)}/procedure-builders/${builderId}/candidates/${candidateId}/draft`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ draft }),
      },
    ),
    (value) => builderSchema.parse(value),
  );
}

export async function publishProcedureCandidates(
  customerId: string,
  facilityId: string,
  builderId: string,
  candidateIds: string[],
) {
  return jsonResponse(
    await workApiFetch(
      `${basePath(customerId, facilityId)}/procedure-builders/${builderId}/publish`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ candidateIds }),
      },
    ),
    (value) => z.object({ published: z.number() }).parse(value),
  );
}

// The view is part of the key so the deleted list can never be served to, or
// leak into, the active Library or the attachment picker.
export function useProceduresQuery(
  search: string,
  view: ProcedureView = "active",
) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const customerId = selectedCustomer?.id;
  const facilityId = selectedFacility?.id;
  const query = useQuery({
    queryKey: ["procedures", customerId, facilityId, search, view],
    queryFn: () => {
      if (!customerId || !facilityId)
        throw new Error("A customer and facility must be selected.");
      return fetchProcedures(customerId, facilityId, search, view);
    },
    enabled: Boolean(customerId && facilityId),
    meta: { errorMessage: "Failed to load Procedures." },
  });
  return { ...query, scopeKey: `${customerId ?? ""}:${facilityId ?? ""}` };
}

export function useProcedureBuilderQuery(builderId: string | undefined) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const customerId = selectedCustomer?.id;
  const facilityId = selectedFacility?.id;
  return useQuery({
    queryKey: ["procedure-builder", customerId, facilityId, builderId],
    queryFn: () => {
      if (!customerId || !facilityId || !builderId)
        throw new Error("A customer, facility, and builder must be selected.");
      return fetchProcedureBuilder(customerId, facilityId, builderId);
    },
    enabled: Boolean(customerId && facilityId && builderId),
    // ProcedureBuilderScreen holds local, unsaved draft/selection state
    // derived from this query's data. A window-focus refetch would be a
    // silent no-op from the user's perspective but still worth avoiding —
    // ProcedureBuilderScreen's own same-builder guard is the real
    // protection against a refetch overwriting local edits.
    refetchOnWindowFocus: false,
    meta: { errorMessage: "Failed to load Procedure Builder." },
  });
}

export function useProcedureDetailQuery(
  procedureId?: string,
  view: ProcedureView = "active",
) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const customerId = selectedCustomer?.id;
  const facilityId = selectedFacility?.id;
  return useQuery({
    queryKey: ["procedure", customerId, facilityId, procedureId, view],
    queryFn: () => {
      if (!customerId || !facilityId || !procedureId)
        throw new Error(
          "A customer, facility, and Procedure must be selected.",
        );
      return fetchProcedureDetail(customerId, facilityId, procedureId, view);
    },
    enabled: Boolean(customerId && facilityId && procedureId),
    meta: { errorMessage: "Failed to load Procedure." },
  });
}

type FacilityAssetNode = {
  assetTreeId: number;
  name: string;
  code: string;
  children: FacilityAssetNode[];
};

const assetTreeNodeSchema: z.ZodType<FacilityAssetNode> = z.lazy(() =>
  z.object({
    assetTreeId: z.number().int().positive(),
    name: z.string(),
    code: z.string(),
    children: z.array(z.lazy(() => assetTreeNodeSchema)).default([]),
  }),
) as z.ZodType<FacilityAssetNode>;

function flattenAssetTree(
  nodes: FacilityAssetNode[],
): Array<{ id: number; name: string }> {
  const result: Array<{ id: number; name: string }> = [];

  function traverse(node: FacilityAssetNode) {
    result.push({ id: node.assetTreeId, name: node.name });
    if (node.children && Array.isArray(node.children)) {
      node.children.forEach(traverse);
    }
  }

  nodes.forEach(traverse);
  return result;
}

export async function fetchFacilityAssets(
  customerId: string,
  facilityId: string,
) {
  return jsonResponse(
    await workApiFetch(`${basePath(customerId, facilityId)}/asset-tree`),
    (value) => {
      const result = z
        .object({
          nodes: z.array(assetTreeNodeSchema),
        })
        .parse(value);
      return flattenAssetTree(result.nodes);
    },
  );
}

export function useFacilityAssetsQuery() {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const customerId = selectedCustomer?.id;
  const facilityId = selectedFacility?.id;
  return useQuery({
    queryKey: ["facility-assets", customerId, facilityId],
    queryFn: () => {
      if (!customerId || !facilityId)
        throw new Error("A customer and facility must be selected.");
      return fetchFacilityAssets(customerId, facilityId);
    },
    enabled: Boolean(customerId && facilityId),
    meta: { errorMessage: "Failed to load Assets." },
  });
}

export async function createProcedureFromForm(
  customerId: string,
  facilityId: string,
  data: {
    title: string;
    assetId: string;
    type: string;
    frequency: string;
    description: string;
  },
) {
  const form = new FormData();
  form.append("title", data.title);
  form.append("assetId", data.assetId);
  form.append("type", data.type);
  form.append("frequency", data.frequency);
  form.append("description", data.description);

  return jsonResponse(
    await workApiFetch(
      `${basePath(customerId, facilityId)}/procedures/create`,
      { method: "POST", body: form },
    ),
    (value) => builderSchema.parse(value),
  );
}
